import React, {createContext, useContext, useReducer} from "react";
import {useTranslation} from "react-i18next";
import {ProgressIndicator} from "../app/ProgressIndicator";
import {decamelize} from "../app/decamelize";

export const SET_QUERY = "searchContext/setQuery";
export const ADD_FQ = "searchContext/addFq";
export const CLEAR_SEARCH_AND_ADD_FQ = "searchContext/clearSearchAndAddFq";
export const UPDATE_FQ = "searchContext/updateFq";
export const REMOVE_FQ = "searchContext/removeFq";
export const CLEAR_FQ = "searchContext/clearFq";
export const SET_SORT = "searchContext/setSearch";
export const SET_ROWS = "searchContext/setRows";
export const SET_START = "searchContext/setStart";
export const SET_SEARCHING = "searchContext/setSearching";
export const SET_ERROR = "searchContext/setError";
export const SET_RESULT = "searchContext/setResult";
export const CLEAR_FILTERS = "searchContext/clearFilters";
export const CLEAR_FILTERS_DONE = "searchContext/clearFilterDone";
export const SHOW_FILTERS = "searchContext/showFilters";
export const HIDE_FILTERS = "searchContext/hideFilters";
export const CLEAR_RESULTS = "searchContext/clearResults";
export const SET_SELECTED = "searchContext/setSelected";
export const REMOVE_SELECTED = "searchContext/removeSelected";
export const SELECT = "searchContext/select";
export const SELECT_MODELS = "searchContext/selectModels";
export const SELECT_ALL = "searchContext/selectAll";
export const UNSELECT = "searchContext/unselect";
export const UNSELECT_ALL = "searchContext/unselectAll";
export const MODELS_SAVED = "searchContext/modelsSaved";
export const REMOVE_FROM_RESULTS = "searchContext/removeFromResults";
export const SEARCH_ONLY_MINE = "searchContext/searchOnlyMine";
export const PROJECT_CONTEXT = "searchContext/projectContext";
export const NOT_PROJECT_CONTEXT = "searchContext/notProjectContext";
export const CLEAR_ONLY_MINE = "searchContext/clearOnlyMine";
export const SET_ONLY_MINE = "searchContext/setOnlyMine";

export const SHOW_INSTANTLY = "searchContext/showInstantly";

export const SET_DELETING = 'searchContext/setDeleting';
export const CLEAR_DELETING = 'searchContext/clearDeleting';

export const OPEN_PUBLISHING_TAGS_DIALOG = 'searchContext/openPublishingTagsDialog';
export const CLOSE_PUBLISHING_TAGS_DIALOG = 'searchContext/closePublishingTagsDialog';

const SearchStateContext = createContext(undefined);
const SearchDispatchContext = createContext(undefined);
const SearchTranslationContext = createContext(undefined);

const initialState = {
    query: "",
    timestamp: undefined,
    folderContext: false,
    sort: "created_at desc",
    fl: null,
    start: 0,
    rows: 10,
    expand: true,
    facetField:
        "persons.reference.title,producer.reference.title,places.reference.name_and_title," +
        "licenses.reference.title,subjects.reference.title,created_by_name,languages.title,document_type," +
        "mediae.reference.mime_type,mediae.reference.duration_seconds,archeologyprojectref.type," +
        "generalprojectref.type,archeologyprojectref.active,generalprojectref.active," +
        "copyright_type,copyright_info.title,reference_types.ref_type.title,publish.tags.reference.title," +
        "publish.tags.last_status",

    statsField:
        "created_at,updated_at,production_date,mediae.reference.duration_seconds",
    fq: [],
    projectContext: false,
    results: {
        count: -1,
        facets: {
            persons: {},
            producers: {},
            places: {},
            licenses: {},
            subjects: {},
            creators: {},
            languages: {},
            mimeTypes: {},
            durationSeconds: {},
            projectTypes: {},
            projectStates: {},
            documentTypes: {},
            copyrightTypes: {},
            copyrightClauses: {},
            publishTagsReferenceTitle: {},
            publishTagsLastStatus: {}
        },
        stats: {
            createdAt: {},
            updatedAt: {},
            productionDate: {},
        },
        models: [],
        error: false,
        modelsOrder: {},
    },
    clearSelection: false,
    searching: false,
    filtersHidden: false,
    selected: [],
    triggerSearch: 0,
    removedFromResults: {seq: 0, models: []},
    onlyMine: false,
    deleting: false,
    clearFilters: false,
    showInstantly: null,
    openPublishingTagsDialog: false,
};

const updateLatestChanges = (old, updated) => {
    const oldIds = new Set(old.map((o) => o.uniqueId));
    const updatedIds = new Set(updated.map((u) => u.uniqueId));

    return [
        ...updated.filter((model) => oldIds.has(model.uniqueId)),
        ...old.filter((o) => !updatedIds.has(o.uniqueId)),
    ];
};

const buildModelsOrder = (models) =>
    models
        .filter((f) => f.status !== "project")
        .reduce(
            (acc, cur, idx) => {
                // Direct assignment is O(n); spreading inside reduce is O(n²).
                acc[idx] = cur.uniqueId;
                return acc;
            },
            {}
        );

const nextRemovedFromResults = (previous, matchedModels) =>
    matchedModels.length === 0 ? previous : {seq: previous.seq + 1, models: matchedModels};

const removeFromResults = (state, removed) => {
    const removedIds = new Set(
        removed.filter((m) => m.uniqueId != null).map((m) => m.uniqueId)
    );
    const models = state.results.models.filter((m) => !removedIds.has(m.uniqueId));
    const matched = state.results.models.filter((m) => removedIds.has(m.uniqueId));
    const removedCount = matched.length;

    return {
        ...state,
        removedFromResults: nextRemovedFromResults(state.removedFromResults, matched),
        results: {
            ...state.results,
            models,
            count: state.results.count - removedCount,
            modelsOrder: buildModelsOrder(models),
        },
        selected: state.selected.filter((m) => !removedIds.has(m.uniqueId)),
    };
};

const searchReducer = (state, action) => {
    switch (action.type) {
        case OPEN_PUBLISHING_TAGS_DIALOG:
            return {
                ...state,
                openPublishingTagsDialog: true
            };
        case CLOSE_PUBLISHING_TAGS_DIALOG:
            return {
                ...state,
                openPublishingTagsDialog: false
            };
        case SHOW_INSTANTLY:
            return {
                ...state,
                showInstantly: action.showInstantly
            };
        case SET_QUERY:
            return {
                ...state,
                query: action.query,
                timestamp: action?.timestamp || undefined,
                folderContext: action.folderContext,
                results: {
                    ...state.results,
                    count: -1
                },
            };
        case SET_SORT:
            return {
                ...state,
                sort: decamelize(action.sort),
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_ROWS:
            return {
                ...state,
                start: 0,
                rows: action.rows,
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_START:
            return {
                ...state,
                start: action.start,
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case ADD_FQ:
            return {
                ...state,
                fq: [...state.fq, action.fq],
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case CLEAR_SEARCH_AND_ADD_FQ:
            return {
                ...state,
                query: "*",
                onlyMine: state.onlyMine,
                fq: [action.fq],
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case UPDATE_FQ:
            return {
                ...state,
                fq: [
                    ...state.fq.filter((singleFq) => singleFq.key !== action.fq.key),
                    action.fq,
                ],
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case REMOVE_FQ:
            return {
                ...state,
                fq: state.fq.filter(
                    (field) =>
                        !(field.key === action.fq.key && field.value === action.fq.value)
                ),
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case CLEAR_FQ:
            return {
                ...state,
                fq: [],
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_SEARCHING:
            return {
                ...state,
                searching: true,
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_RESULT:
            return {
                ...state,
                results: {
                    count: action.count,
                    facets: action.facets,
                    models: action.models,
                    stats: action.stats,
                    error: false,
                    modelsOrder: buildModelsOrder(action.models),
                },
                searching: false,
            };
        case CLEAR_FILTERS:
            if (!state.projectContext) {
                return {
                    ...state,
                    query: '*',
                    fq: state.fq.filter(
                        (field) =>
                            "folders.reference.title" === field.key ||
                            "folder_ids" === field.key
                    ),
                    start: 0,
                    clearFilters: true,
                    results: {
                        ...state.results,
                        count: -1
                    }
                };
            } else if (state.projectContext) {
                let fq = state.fq.filter(
                    (field) =>
                        "folders.reference.title" === field.key ||
                        "folder_ids" === field.key
                );
                return {
                    ...state,
                    query: '*',
                    fq: fq,
                    start: 0,
                    clearFilters: true,
                    results: {
                        ...state.results,
                        count: -1
                    }
                };
            }
            break;
        case CLEAR_FILTERS_DONE:
            return {
                ...state,
                clearFilters: false,
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_ERROR:
            return {
                ...state,
                searching: false,
                results: {
                    ...initialState.results,
                    error: true,
                },
            };
        case SHOW_FILTERS:
            return {
                ...state,
                filtersHidden: false,
            };
        case HIDE_FILTERS:
            return {
                ...state,
                filtersHidden: true,
            };
        case CLEAR_RESULTS:
            return {
                ...state,
                results: initialState.results,
            };
        case SET_SELECTED:
            return {
                ...state,
                clearSelection: false,
                selected: action.selected
            };
        case REMOVE_SELECTED: {
            // Build a Set once so each .has() call is O(1).  The previous
            // action.uniqueIds.includes() was O(|uniqueIds|) per item, making
            // the whole filter O(|selected| × |uniqueIds|).
            const removeSet = new Set(action.uniqueIds);
            return {
                ...state,
                selected: state.selected.filter(model => !removeSet.has(model.uniqueId)),
            };
        }
        case SELECT:
            return {
                ...state,
                selected: [...state.selected, action.model],
            };
        case SELECT_MODELS:
            return {
                ...state,
                selected: [...action.models],
            };
        case SELECT_ALL:
            return {
                ...state,
                clearSelection: false,
                selected: state.results.models,
            };
        case UNSELECT:
            return {
                ...state,
                clearSelection: false,
                selected: state.selected.filter(
                    (model) => model.uniqueId !== action.model.uniqueId
                ),
            };
        case UNSELECT_ALL:
            return {
                ...state,
                clearSelection: true,
                selected: [],
            };
        case MODELS_SAVED:
            return {
                ...state,
                selected: updateLatestChanges(state.selected, action.models),
                triggerSearch: state.triggerSearch + 1,
            };
        case REMOVE_FROM_RESULTS:
            return removeFromResults(state, action.models);
        case SEARCH_ONLY_MINE:
            return {
                ...state,
                onlyMine: true,
                query: "*",
                results: {
                    ...state.results,
                    count: -1
                }
            };
        case SET_ONLY_MINE:
            return {
                ...state,
                onlyMine: true
            };
        case CLEAR_ONLY_MINE:
            return {
                ...state,
                onlyMine: false
            };
        case PROJECT_CONTEXT:
            return {
                ...state,
                projectContext: true,
            };
        case NOT_PROJECT_CONTEXT:
            return {
                ...state,
                projectContext: false,
            };
        case SET_DELETING:
            return {
                ...state,
                deleting: true
            };
        case CLEAR_DELETING:
            return {
                ...state,
                deleting: false
            };
        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
};

export const SearchProvider = ({children}) => {
    const [state, dispatch] = useReducer(searchReducer, initialState, undefined);
    const {t, ready} = useTranslation("dams.search", {useSuspense: false});
    if (!ready && !import.meta.env.VITEST) {
        return <ProgressIndicator/>;
    }

    return (
        <SearchTranslationContext.Provider value={t}>
            <SearchStateContext.Provider value={state}>
                <SearchDispatchContext.Provider value={dispatch}>
                    {children}
                </SearchDispatchContext.Provider>
            </SearchStateContext.Provider>
        </SearchTranslationContext.Provider>
    );
};

export const useSearchState = () => {
    const context = useContext(SearchStateContext);
    if (undefined === context) {
        throw new Error("useSearchState must be used within a SearchProvider");
    } else {
        return context;
    }
};

export const useSearchDispatch = () => {
    const context = useContext(SearchDispatchContext);
    if (undefined === context) {
        throw new Error("useSearchDispatch must be used withing a SearchProvider");
    } else {
        return context;
    }
};

export const useSearchTranslation = () => {
    const context = useContext(SearchTranslationContext);
    if (undefined === context) {
        throw new Error(
            "useSearchTranslation must be used within an SearchProvider."
        );
    } else {
        return context;
    }
};
